import { apiResourceUrl, isNotNull, legacyApiResourceUrl } from "@ploy-lib/core";
import { BaseResource, BasePlainCamelCasedResource } from "@ploy-lib/rest-resources";
import * as ContentComponents from "../content";
import * as ContentComponentsDeprecated from "../content/deprecated";
import { isControlComponent } from "../property-controls";
import { propertyValues } from "../property-controls/propertyValues";
import { v4 as uuidv4 } from "uuid";
export const defaultColumns = {
    md: 12,
    sm: 10,
    xs: 6
};
class VulcanResource extends BasePlainCamelCasedResource {
    pk() {
        return "vulcan";
    }
    static vulcan() {
        const endpoint = this.detail();
        return endpoint.extend({
            schema: this,
            url: () => this.listUrl(),
            fetch: async () => {
                const jsonResponse = await endpoint.fetch({});
                return jsonResponse?.initialData;
            }
        });
    }
    static get urlRoot() {
        return legacyApiResourceUrl(`${this.page}/Vulcan`);
    }
}
class FrontPageVulcanResource extends VulcanResource {
    constructor() {
        super(...arguments);
        this.listOfContent = [];
    }
}
FrontPageVulcanResource.page = "FrontPage";
class DashboardAdminResource extends BasePlainCamelCasedResource {
    constructor() {
        super(...arguments);
        this.name = "";
        this.configuredWidgets = [];
    }
    pk() {
        return this.dashboardId?.toString();
    }
    static list() {
        const endpoint = super.list().extend({
            schema: [this],
            url: () => `${this.urlRoot}/GetDashboardList`
        });
        return endpoint.extend({
            fetch: (params) => endpoint.fetch(params)
        });
    }
    static detail() {
        const endpoint = super.detail().extend({
            schema: this,
            url: params => this.listUrl(params).replace(this.urlRoot, `${this.urlRoot}/Setup`)
        });
        return endpoint.extend({
            fetch: async (params) => {
                const data = await endpoint.fetch(params);
                return data.dashboard;
            }
        });
    }
}
DashboardAdminResource.urlRoot = legacyApiResourceUrl("Admin/DashboardAdmin");
export class DashboardGridResource extends BaseResource {
    constructor() {
        super(...arguments);
        this.id = undefined;
        this.name = "";
        this.layouts = {};
        this.items = {};
        this.columns = defaultColumns;
        this._legacyId = undefined;
    }
    pk() {
        return this._legacyId
            ? `LEGACY: ${this._legacyId.toString()}`
            : this.id?.toString();
    }
    static list() {
        const legacyEndpoint = DashboardAdminResource.list();
        const endpoint = super.list();
        return endpoint.extend({
            schema: [this],
            fetch: async (params) => {
                const [data = [], legacyData] = await Promise.all([
                    endpoint.fetch(params).catch(() => { }),
                    legacyEndpoint.fetch({})
                ]);
                return [
                    ...data,
                    ...legacyData
                        .filter(d => !data.some(x => x._legacyId === d.dashboardId))
                        .map(d => ({
                        id: undefined,
                        _legacyId: d.dashboardId,
                        name: d.name
                    }))
                ];
            }
        });
    }
    static detail() {
        const fpVulcanEndpoint = FrontPageVulcanResource.vulcan();
        const legacySetupEndpoint = DashboardAdminResource.detail();
        const endpoint = super.detail();
        return endpoint.extend({
            schema: this,
            fetch: async ({ _legacyId, ...params }) => {
                const hasParams = Object.values(params).filter(isNotNull).length > 0;
                if (!hasParams && _legacyId) {
                    const dashboard = await legacySetupEndpoint.fetch({
                        dashboardId: _legacyId
                    });
                    return convertLegacyGrid(dashboard.configuredWidgets, dashboard.dashboardId ?? _legacyId);
                }
                try {
                    return await endpoint.fetch(params);
                }
                catch (e) {
                    try {
                        if (params.username || params.salespersonUsername) {
                            const fpVulcan = await fpVulcanEndpoint.fetch();
                            if (fpVulcan) {
                                return convertLegacyGrid(fpVulcan?.listOfContent ?? [], `user: ${params.username}`, `user: ${params.username}`);
                            }
                        }
                    }
                    catch { }
                    throw e;
                }
            }
        });
    }
}
DashboardGridResource.urlTemplates = [
    apiResourceUrl("user/{username}/dashboard-grid"),
    apiResourceUrl("salesperson/{salespersonUsername}/dashboard-grid")
];
DashboardGridResource.urlRoot = apiResourceUrl("dashboard-grids");
function convertLegacyGrid(legacyGrid, id, name) {
    const legacyItems = getItems(legacyGrid);
    const { layouts, columns } = itemsToLayouts(legacyItems);
    const items = Object.fromEntries(legacyItems
        .map((x) => {
        const component = fixDeprecatedName(x.name);
        if (!component) {
            console.warn(`Unsupported grid component '${x.name}' in dashboard '${name}'.\nId: ${x.id}\nProps:`, x.properties);
            return null;
        }
        const properties = fixProperties(component, x.properties);
        return [
            x.id,
            {
                id: x.id,
                component,
                properties
            }
        ];
    })
        .filter(isNotNull));
    if (name) {
        return {
            id: undefined,
            _legacyId: id,
            name,
            layouts,
            items,
            columns
        };
    }
    return {
        id: undefined,
        _legacyId: id,
        layouts,
        items,
        columns
    };
}
function isDeprecated(name) {
    return Boolean(ContentComponentsDeprecated[name]);
}
function isGridComponent(name) {
    return Boolean(ContentComponents[name]);
}
const componentNameMap = new Map(Object.entries(ContentComponents).map(([k, v]) => [v, k]));
function fixDeprecatedName(name) {
    if (isDeprecated(name)) {
        const Deprecated = ContentComponentsDeprecated[name];
        const fixedName = componentNameMap.get(Deprecated);
        return fixedName ?? name;
    }
    if (isGridComponent(name))
        return name;
    return null;
}
function fixProperties(name, properties) {
    const Component = ContentComponents[name];
    if (isControlComponent(Component)) {
        return propertyValues(Component.propertyControls, properties);
    }
    return properties;
}
const boxClassToGridProps = (boxClass, columns) => {
    const classes = boxClass.split(" ");
    const contentBox = classes.find(x => x.includes("content-box"));
    if (!contentBox)
        return;
    const [, , size, bp = size] = contentBox.split("-", 4);
    switch (bp) {
        case "small":
            return {
                xs: columns.xs,
                sm: Math.round(columns.sm / 2),
                md: Math.round(columns.md / 3)
            };
        case "medium":
            return {
                xs: columns.xs,
                sm: Math.round(columns.sm / 2),
                md: Math.round(columns.md / 2)
            };
        case "large":
            return {
                xs: columns.xs,
                sm: columns.sm,
                md: columns.md
            };
        default:
            const md = Math.max(1, Math.min(parseInt(size), columns.md));
            return {
                md,
                sm: md < columns.md / 2 ? columns.sm / 2 : columns.sm,
                xs: columns.xs
            };
    }
};
const boxClassToBoxProps = (boxClass) => {
    const classes = boxClass.split(" ");
    return classes.includes("grey")
        ? { backgroundColor: "grey.200", headerColor: "grey.500" }
        : {};
};
function getItems(legacyGrid) {
    return (legacyGrid
        .filter(isNotNull)
        .flatMap(row => row.filter(isNotNull).map(({ name, boxProps = {}, properties }) => {
        const { boxHeader, header, iconClass, boxClass, ...rest } = properties ?? {};
        if (boxClass) {
            boxProps = {
                ...boxClassToBoxProps(boxClass),
                ...boxProps
            };
        }
        if (header ?? boxHeader)
            boxProps = {
                header: header ?? boxHeader,
                ...boxProps
            };
        if (iconClass)
            boxProps = {
                iconClass,
                ...boxProps
            };
        return {
            name,
            boxClass,
            properties: {
                boxProps,
                ...rest
            }
        };
    }))
        .map((item, i) => ({ ...item, id: uuidv4() })) ?? []);
}
function gridToLayout(partials = [], cols = 12, defaultWidth = 4) {
    let accX = 0;
    let accY = 0;
    const defaultHeight = 7;
    return partials.map(l => {
        const w = Math.min(l.w ?? defaultWidth, l.maxW ?? cols, cols);
        const h = Math.max(l.h ?? defaultHeight, l.minH ?? 1, 1);
        const x = accX + w > cols ? 0 : accX;
        const y = accX + w > cols ? accY + h : accY;
        accX = x + w;
        accY = y;
        return {
            ...l,
            h,
            w,
            x: l.x ?? x,
            y: l.y ?? y
        };
    });
}
function itemsToLayouts(allItems) {
    const toLayoutItem = (brk) => (item) => {
        const cols = item.gridProps?.[brk];
        const w = typeof cols === "number" ? cols : undefined;
        return {
            i: item.id,
            w
        };
    };
    const items = allItems.map(item => ({
        ...item,
        gridProps: item.boxClass
            ? boxClassToGridProps(item.boxClass, defaultColumns)
            : undefined
    }));
    return {
        layouts: {
            md: gridToLayout(items.map(toLayoutItem("md")), defaultColumns.md, defaultColumns.md / 3),
            sm: gridToLayout(items.map(toLayoutItem("sm")), defaultColumns.sm, defaultColumns.sm / 2),
            xs: gridToLayout(items.map(toLayoutItem("xs")), defaultColumns.xs, defaultColumns.xs)
        },
        columns: defaultColumns
    };
}
//# sourceMappingURL=DashboardGridResource.js.map